import {
  connect as connectFormik,
  Field,
  FieldArray,
  FieldProps,
  FormikProps,
  getIn,
} from 'formik';
import React from 'react';
import { v1 as uuid } from 'uuid';
import { IOption } from 'components/Select/Select';
import ActionLink from 'components/ActionLink';
import IconTypes from 'components/Icon/IconTypes';
import Input from 'components/Input';
import notify from 'components/Notify';
import Select from 'components/Select';
import Textarea from 'components/Textarea';
import { removeDuplicates } from 'utils/happyFunctions';
import CKEditorField, {
  CkEditorBaseConfig,
} from '../../../../../CKEditorField/CKEditorField';
import Collapsible from '../../../../../Collapsible/Collapsible';
import { FieldArraysWrapper } from '../styles';
import messages from './Messages';
import {
  Delete,
  Flex,
  HintBoxWrapper,
  HintIcon,
  HintText,
  InputBlock,
  InputTitleWrapper,
  QuestionHintBox,
} from './styles';
import { ICompany } from 'store/companies/companiesTypes';
import {
  IFormValues,
  IMultipleChoiceQuestionView,
  IQuestionField,
  IQuestionsView,
  IQuestionTags,
  ISurveyView,
  QuestionState,
} from 'store/survey/surveyTypes';
import { ITagView } from 'store/providers/providerTypes';
import i18n from '../../../../../../i18n';
import { black, lightYellow } from '../../../../../../utils/style/colors';
import ConfigSelector from '../../../../../ConfigSelector/ConfigSelector';

const getAvailableConfigs = () => {
  return [
    {
      key: 'conf_addDescriptionTitle',
      label:
        i18n.t('conf_addDescriptionTitle', { ns: 'survey' }) ||
        'Add Description Title',
      value: '',
      type: 'text' as const,
    },
    {
      key: 'conf_needToAddAnswer',
      label:
        i18n.t('conf_needToAddAnswer', { ns: 'survey' }) ||
        'Need to add answer',
      value: false,
      type: 'boolean' as const,
    },
    {
      key: 'conf_dataSourceField',
      label:
        i18n.t('conf_dataSourceField', { ns: 'survey' }) || 'Data Source Field',
      value: '',
      type: 'text' as const,
    },
    {
      key: 'conf_dataSourceConvertDateFormat',
      label:
        i18n.t('conf_dataSourceConvertDateFormat', { ns: 'survey' }) ||
        'Data Source Convert Date Format',
      value: '',
      type: 'text' as const,
    },
    {
      key: 'conf_useAsEffectiveDate',
      label:
        i18n.t('conf_useAsEffectiveDate', { ns: 'survey' }) ||
        'Use As Effective Date',
      value: false,
      type: 'boolean' as const,
    },
    {
      key: 'conf_randomDataOnMissing',
      label:
        i18n.t('conf_randomDataOnMissing', { ns: 'survey' }) ||
        'Random Data On Missing',
      value: false,
      type: 'boolean' as const,
    },
    {
      key: 'conf_dataSourceConvertMap',
      label:
        i18n.t('conf_dataSourceConvertMap', { ns: 'survey' }) ||
        'Data Source Field Mapping',
      value: false,
      type: 'text' as const,
    },
    {
      key: 'conf_hidden',
      label: i18n.t('conf_hidden', { ns: 'survey' }) || 'Hidden',
      value: false,
      type: 'boolean' as const,
    },
    {
      key: 'conf_related_field',
      label: i18n.t('conf_related_field', { ns: 'survey' }),
      value: '',
      type: 'text' as const,
    },
    {
      key: 'conf_fullScreen',
      label: i18n.t('conf_fullScreen', { ns: 'survey' }),
      value: '',
      type: 'boolean' as const,
    },
    {
      key: 'conf_useAsSignature',
      label: i18n.t('conf_useAsSignature', { ns: 'survey' }),
      value: 'false',
      type: 'boolean' as const,
      advanced: true,
    },
    {
      key: 'conf_isFollowUp',
      label: i18n.t('conf_isFollowUp', { ns: 'survey' }),
      value: 'true',
      type: 'boolean' as const,
    },
    {
      key: 'conf_addDescriptionButton',
      label: i18n.t('conf_addDescriptionButton', { ns: 'survey' }),
      value: 'true',
      type: 'boolean' as const,
    },
    {
      key: 'conf_keyAsParamsOfResponse',
      label: i18n.t('conf_keyAsParamsOfResponse', { ns: 'survey' }),
      value: '',
      type: 'text' as const,
    },
    {
      key: 'conf_newsComplementoryQuestionForNews',
      label: i18n.t('conf_newsComplementoryQuestionForNews', { ns: 'survey' }),
      value: 'true',
      type: 'boolean' as const,
    },
    {
      key: 'conf_newsQuestionToComplete',
      label: i18n.t('conf_newsQuestionToComplete', { ns: 'survey' }),
      value: 'true',
      type: 'boolean' as const,
    },
    {
      key: 'conf_systemPrompt',
      label: i18n.t('conf_systemPrompt', { ns: 'survey' }),
      value: '',
      type: 'text' as const,
    },
    {
      key: 'conf_enableImageGeneration',
      label: i18n.t('conf_enableImageGeneration', { ns: 'survey' }),
      value: 'true',
      type: 'boolean' as const,
    },
  ];
};

const getSelectedTags = (selectedTags: IOption[]) => {
  return selectedTags.map((tag) => {
    return { text: tag.label, id: tag.value };
  });
};

interface IOwnProps {
  tags: IQuestionTags;
  icon: IconTypes;
  companyRoles?: ICompany['roles'];
  children?: React.ReactNode;
  questionTitleLabel?: string;
  otherQuestions: {
    [questionId: number]: { data: IQuestionsView; state: QuestionState };
  };
  token: string;
  survey: ISurveyView;
  isOwnerOrSupport?: boolean;
  userIsAdvanceAdmin?: boolean;
}

interface IFormikConnect {
  formik: FormikProps<IFormValues>;
}

type IComponentProps = IOwnProps & IFormikConnect;

interface IConfigSelectorState {
  selectedConfigs: Array<{
    key: string;
    label: string;
    value: string | boolean;
    type: 'text' | 'boolean';
  }>;
}

const weightQuestions = [
  'multipleChoice',
  'grid',
  'indicator',
  'multipleAnswer',
];

export const getTags = (
  tags: IQuestionTags,
  removeDeleted: boolean = false,
  parentIds?: number[]
) => {
  return Object.keys(tags).reduce((list: IOption[], tagId: string) => {
    if (removeDeleted && tags[tagId].deleted) return list;
    if (parentIds && !parentIds.includes(tags[tagId].parentId || -1))
      return list;
    list.push({
      label: tags[tagId].text + (tags[tagId].deleted ? messages.deleted : ''),
      value: tags[tagId].id,
    });
    return list;
  }, []);
};
export const getQuestionTags = (
  tags: ITagView[],
  removeDeleted: boolean = false,
  source?: IQuestionTags
) => {
  const finalSource = source ?? tags;
  return tags.reduce((list: IOption[], tag: ITagView) => {
    if (
      !finalSource[tag.id] ||
      (removeDeleted && finalSource[tag.id].deleted)
    ) {
      return list;
    }

    list.push({
      label: finalSource[tag.id].text,
      value: finalSource[tag.id].id,
    });

    return list;
  }, []);
};

class GeneralQuestionEditor extends React.Component<
  IComponentProps,
  IConfigSelectorState
> {
  constructor(props: IComponentProps) {
    super(props);

    const questionData = getIn(props.formik.values, 'data');
    const fields = getIn(questionData, 'fields') || [];
    const availableConfigs = getAvailableConfigs();

    const initialSelectedConfigs = fields
      .filter((field: any) => field.name && field.name.startsWith('conf_'))
      .map((field: any) => {
        const config = availableConfigs.find((c) => c.key === field.name);
        if (config) {
          return {
            key: field.name,
            label: config.label,
            value:
              config.type === 'boolean' ? field.value === 'true' : field.value,
            type: config.type,
          };
        }
        return null;
      })
      .filter(Boolean);

    this.state = {
      selectedConfigs: initialSelectedConfigs,
    };
  }

  handleConfigAdd = (config: {
    key: string;
    label: string;
    value: string | boolean;
    type: 'text' | 'boolean';
  }) => {
    const newSelectedConfigs = [...this.state.selectedConfigs, config];
    this.setState({ selectedConfigs: newSelectedConfigs });
    this.syncConfigsToFormik(newSelectedConfigs);
  };

  handleConfigRemove = (config: {
    key: string;
    label: string;
    value: string | boolean;
    type: 'text' | 'boolean';
  }) => {
    const newSelectedConfigs = this.state.selectedConfigs.filter(
      (c) => c.key !== config.key
    );
    this.setState({ selectedConfigs: newSelectedConfigs });
    this.syncConfigsToFormik(newSelectedConfigs);
  };

  handleConfigValueChange = (configKey: string, newValue: string | boolean) => {
    const newSelectedConfigs = this.state.selectedConfigs.map((config) =>
      config.key === configKey ? { ...config, value: newValue } : config
    );
    this.setState({ selectedConfigs: newSelectedConfigs });
    this.syncConfigsToFormik(newSelectedConfigs);
  };

  syncConfigsToFormik = (
    configs: Array<{
      key: string;
      label: string;
      value: string | boolean;
      type: 'text' | 'boolean';
    }>
  ) => {
    const { formik } = this.props;
    const currentFields = getIn(formik.values, ['data', 'fields']) || [];

    // Remove existing conf_ fields
    const nonConfigFields = currentFields.filter(
      (field: any) => !field.name || !field.name.startsWith('conf_')
    );

    // Add new config fields
    const configFields = configs.map((config) => ({
      name: config.key,
      value: config.type === 'boolean' ? config.value.toString() : config.value,
    }));

    const newFields = [...nonConfigFields, ...configFields];
    formik.setFieldValue('data.fields', newFields);
  };

  handleChangeDistinctive =
    (
      onChange: (e: React.ChangeEvent<any>) => void,
      multiSelect: boolean,
      form: FormikProps<IFormValues>
    ) =>
    (e: React.ChangeEvent<any>) => {
      if (e.target.checked) {
        form.setFieldValue('data.disabled', false);
      }
      if (multiSelect) {
        notify(
          messages.aQuestoinCouldNotBeDistinctiveAndMultiSelectAtSameTime,
          'warning'
        );
        return false;
      } else {
        onChange(e);
        return true;
      }
    };

  hintInputId = uuid();
  titleInputId = uuid();
  disabledInputId = uuid();
  weightInputId = uuid();
  distinctiveId = uuid();
  showInTitleId = uuid();
  requireInputId = uuid();
  viewConditionCheckbox = uuid();
  unifyValueAndText = uuid();
  readFromItemTag = uuid();

  render() {
    const {
      tags,
      icon,
      formik: { values: question },
      questionTitleLabel,
      otherQuestions,
      children,
      token,
      survey,
      isOwnerOrSupport,
      userIsAdvanceAdmin,
    } = this.props;
    const isExam = survey.surveyType === 'Exam' || survey.surveyType === 'Bank';
    const questionData = getIn(question, 'data');
    const fields = getIn(question, ['data', 'fields']);
    const multiSelect = getIn(question, ['data', 'multiSelect']);
    const useTags = getIn(question, ['data', 'useTags']);
    const isActive = !getIn(question, ['data', 'disabled']);

    const refQuestionData =
      questionData.viewCondition &&
      questionData.viewCondition.refQuestionId &&
      (otherQuestions[questionData.viewCondition.refQuestionId]
        .data as IMultipleChoiceQuestionView);
    let conditionChoices: IMultipleChoiceQuestionView['choiceList'] = [];
    if (refQuestionData && refQuestionData.useTags && tags) {
      if (refQuestionData.parentTagChoices) {
        refQuestionData.parentTagChoices.forEach((parentId: number) => {
          Object.values(tags)
            .filter(
              (tag) =>
                tag.parentId === parentId &&
                !tag.deleted &&
                !refQuestionData.excludedTagChoices?.includes(tag.id)
            )
            .map((item: any) =>
              conditionChoices.push({
                value: item.id.toString(),
                text: item.text,
                score: null,
              })
            );
        });
      }
      if (refQuestionData.tagChoices) {
        refQuestionData.tagChoices.map((item: any) =>
          conditionChoices.push({
            value: tags[item].id.toString(),
            text: tags[item].text,
            score: null,
          })
        );
      }
      // number[]
      conditionChoices = removeDuplicates(conditionChoices);
    } else if (refQuestionData) {
      conditionChoices = refQuestionData.choiceList;
    }

    const companyRoleChoices: IOption[] =
      Object.values(this.props.companyRoles || {}).map((value) => {
        return {
          label: value!.name,
          value: 'R' + value!.id,
        };
      }) ?? [];
    companyRoleChoices.splice(
      0,
      0,
      {
        label: i18n.t('senderWhileSending', { ns: 'survey' }) as string,
        value: '@senderWhileSending',
      },
      {
        label: i18n.t('senderWhileUpdating', { ns: 'survey' }) as string,
        value: '@senderWhileUpdating',
      }
    );
    return (
      <div style={{ opacity: isActive ? '1' : '0.7' }}>
        <InputTitleWrapper>
          <Field
            name="data.title"
            render={({ field }: FieldProps) => (
              <React.Fragment>
                <Input
                  style={{ fontWeight: 'bold' }}
                  {...field}
                  id={this.titleInputId}
                  icon={icon}
                  type="text"
                  label={
                    questionTitleLabel ||
                    (i18n.t('questionTitleLabel', { ns: 'survey' }) as string)
                  }
                  required={true}
                  autoFocus={true}
                  autoComplete="off"
                  wrapperClassName="question-title-input"
                  placeholder={
                    questionTitleLabel ||
                    (i18n.t('questionTitlePlaceholder', {
                      ns: 'survey',
                    }) as string)
                  }
                />
              </React.Fragment>
            )}
          />
          {weightQuestions.includes(questionData.type) && (
            <Field
              name="data.weight"
              render={({ field }: FieldProps) => (
                <React.Fragment>
                  <Input
                    {...field}
                    autoComplete="off"
                    defaultValue="1"
                    disabled={useTags}
                    id={this.weightInputId}
                    label={i18n.t('questionWeight', { ns: 'survey' }) as string}
                    placeholder={
                      i18n.t('questionPlaceholder', { ns: 'survey' }) as string
                    }
                    required={true}
                    step="any"
                    style={{ textAlign: 'center' }}
                    type="number"
                    min=""
                    wrapperClassName="question-input-small"
                  />
                </React.Fragment>
              )}
            />
          )}
        </InputTitleWrapper>
        <Collapsible
          header={
            isExam
              ? (i18n.t('hint', { ns: 'survey' }) as string)
              : (i18n.t('hintTagsAndDesc', {
                  ns: 'survey',
                }) as string)
          }
          iconName="tag"
        >
          <>
            <Field
              name="data.hint"
              render={({ field, form }: FieldProps) => (
                <React.Fragment>
                  <CKEditorField
                    style={{ '--ck-focus-ring': '0px' }}
                    data={field.value}
                    // updateContent={(content: string) => {
                    //   form.setFieldValue('data.hint', content);
                    // }}
                    onChange={(data: any, _: any) => {
                      form.setFieldValue('data.hint', data);
                      // const data = editor.getData();
                      // form.setFieldValue(
                      //     'data.hint',
                      //     getSelectedTags(data)
                      // );;
                    }}
                    dir="rtl"
                    config={CkEditorBaseConfig(token)}
                  />
                </React.Fragment>
              )}
            />
            {!isExam && (
              <Field
                name="data.tags"
                type="select"
                render={({ form }: FieldProps) => (
                  <Select
                    multiChoice={true}
                    label={
                      i18n.t('enterQuestionTag', {
                        ns: 'survey',
                      }) as string
                    }
                    options={tags ? getTags(tags, true) : []}
                    values={
                      questionData.tags
                        ? getQuestionTags(questionData.tags, false, tags)
                        : []
                    }
                    onChangeValues={(selectedTags) => {
                      form.setFieldValue(
                        'data.tags',
                        getSelectedTags(selectedTags)
                      );
                    }}
                    letAddChip={true}
                    placeholder={
                      i18n.t('selectTag', {
                        ns: 'survey',
                      }) as string
                    }
                  />
                )}
              />
            )}
            {!isExam && (
              <FieldArray name="data.fields">
                {({ push, remove }) => (
                  <FieldArraysWrapper>
                    <ActionLink
                      color="blue"
                      onClick={() => push({ name: '', value: '' })}
                    >
                      {
                        i18n.t('addDescription', {
                          ns: 'survey',
                        }) as string
                      }
                    </ActionLink>
                    {fields &&
                      fields
                        .filter(
                          (item: IQuestionField) =>
                            !item.name.startsWith('conf_')
                        )
                        .map((item: IQuestionField, index: number) => (
                          <Flex key={`data.fields[${index}]name`}>
                            <Delete
                              type="x"
                              color="red"
                              onClick={() => remove(index)}
                            />

                            <Field name={`data.fields.${index}.name`}>
                              {({ field }: FieldProps) => (
                                <Input
                                  {...field}
                                  id={`item[${index}]name`}
                                  type="text"
                                  required={true}
                                  placeholder={
                                    i18n.t('questionFieldName', {
                                      ns: 'survey',
                                    }) as string
                                  }
                                  autoComplete="off"
                                  wrapperClassName="title-field"
                                />
                              )}
                            </Field>
                            {item.options ? (
                              <Field
                                name={`data.fields.${index}.value`}
                                type="select"
                                render={({ form, field }: FieldProps) => (
                                  <Select
                                    options={item.options!.reduce(
                                      (options: IOption[], option: string) => {
                                        options.push({
                                          value: option,
                                          label: option,
                                        });
                                        return options;
                                      },
                                      []
                                    )}
                                    values={
                                      field.value
                                        ? field.value
                                            .split(',')
                                            .map((field: string) => {
                                              return {
                                                value: field,
                                                label: field,
                                              };
                                            })
                                        : []
                                    }
                                    onChangeValues={(fieldOption) => {
                                      form.setFieldValue(
                                        `data.fields.${index}.value`,
                                        fieldOption.reduce((current, item) => {
                                          if (current) current += ',';
                                          current += item.value.toString();
                                          return current;
                                        }, '')
                                      );
                                    }}
                                    multiChoice={true}
                                    placeholder={
                                      i18n.t('selectValue', {
                                        ns: 'survey',
                                      }) as string
                                    }
                                  />
                                )}
                              />
                            ) : (
                              <Field name={`data.fields.${index}.value`}>
                                {({ field }: FieldProps) => {
                                  return (
                                    /*<Input
                                        {...field}
                                        id={`item[${index}]value`}
                                        type="text"
                                        placeholder={messages.questionFieldValue}
                                        autoComplete="off"
                                        wrapperClassName="value-field"
                                      />*/
                                    <Textarea
                                      {...field}
                                      id={`item[${index}]value`}
                                      style={{ resize: 'none' }}
                                      placeholder={
                                        i18n.t('questionFieldValue', {
                                          ns: 'survey',
                                        }) as string
                                      }
                                      autoComplete="off"
                                    />
                                  );
                                }}
                              </Field>
                            )}
                          </Flex>
                        ))}

                    <ConfigSelector
                      availableConfigs={getAvailableConfigs().filter(
                        (c) =>
                          !this.state.selectedConfigs.find(
                            (s) => s.key === c.key
                          )
                      )}
                      selectedConfigs={this.state.selectedConfigs}
                      onAdd={this.handleConfigAdd}
                      onRemove={this.handleConfigRemove}
                      onValueChange={this.handleConfigValueChange}
                    />
                    {/*{isOwnerOrSupport && (*/}
                    {/*  <div>*/}
                    {/*    <QuestionHintBox>*/}
                    {/*      <HintIcon type="info" />*/}
                    {/*      <HintText>*/}
                    {/*        <span>*/}
                    {/*          {messages.availableQuestionFieldConfigs}*/}
                    {/*        </span>*/}
                    {/*      </HintText>*/}
                    {/*    </QuestionHintBox>*/}
                    {/*  </div>*/}
                    {/*)}*/}
                  </FieldArraysWrapper>
                )}
              </FieldArray>
            )}
          </>
        </Collapsible>
        {!isExam && (
          <>
            <div className="row">
              {['multipleChoice', 'text'].includes(questionData.type) && (
                <HintBoxWrapper className="col m6 s12 l6">
                  {/**/}
                  <Field
                    name="data.distinctive"
                    render={({ field, form }: FieldProps) => (
                      <React.Fragment>
                        <Input
                          {...field}
                          id={this.distinctiveId}
                          type="checkbox"
                          label={
                            i18n.t('distinctiveLabel', {
                              ns: 'survey',
                            }) as string
                          }
                          checked={questionData && questionData.distinctive}
                          onChange={(props) => {
                            this.handleChangeDistinctive(
                              field.onChange,
                              multiSelect,
                              form
                            )(props);
                          }}
                        />
                      </React.Fragment>
                    )}
                  />
                  <QuestionHintBox>
                    <HintIcon type="info" />
                    <HintText>
                      <span>
                        {
                          i18n.t('useKeepUnique', {
                            ns: 'survey',
                          }) as string
                        }
                      </span>
                      {/*<span>*/}
                      {/*  {*/}
                      {/*    i18n.t('distinctiveHint', {*/}
                      {/*      ns: 'survey',*/}
                      {/*    }) as string*/}
                      {/*  }*/}
                      {/*</span>*/}
                    </HintText>
                  </QuestionHintBox>
                </HintBoxWrapper>
              )}
              {(questionData.type === 'multipleChoice' ||
                questionData.type === 'text') &&
                (isOwnerOrSupport || userIsAdvanceAdmin) && (
                  <HintBoxWrapper className="col m6 s12 l6">
                    {/**/}
                    <Field
                      name="data.showInTitle"
                      render={({ field }: FieldProps) => (
                        <React.Fragment>
                          <Input
                            {...field}
                            id={this.showInTitleId}
                            type="checkbox"
                            label={
                              i18n.t('showInTitleLabel', {
                                ns: 'survey',
                              }) as string
                            }
                            checked={questionData && questionData.showInTitle}
                          />
                        </React.Fragment>
                      )}
                    />
                    <QuestionHintBox>
                      <HintIcon type="info" />
                      <HintText>
                        <span>
                          {
                            i18n.t('showInTitleDesc', {
                              ns: 'survey',
                            }) as string
                          }
                        </span>
                      </HintText>
                    </QuestionHintBox>
                  </HintBoxWrapper>
                )}
            </div>

            {questionData.modificationType === 'deleted' && (
              <Input
                id={`${Math.random()}`}
                type={'checkbox'}
                disabled={true}
                checked={true}
                label={i18n.t('deleted', { ns: 'survey' }) as string}
              />
            )}

            {questionData.modificationDesc && (
              <QuestionHintBox
                style={{ background: lightYellow }}
                color={black}
              >
                <HintIcon type="info" />
                <HintText>
                  <span>{questionData.modificationDesc}</span>
                </HintText>
              </QuestionHintBox>
            )}

            <Field
              name="data.answerRequired"
              render={({ field }: FieldProps) => (
                <React.Fragment>
                  <Input
                    {...field}
                    id={this.requireInputId}
                    type="checkbox"
                    label={
                      i18n.t('answerRequiredLabel', {
                        ns: 'survey',
                      }) as string
                    }
                    checked={questionData && questionData.answerRequired}
                  />
                </React.Fragment>
              )}
            />
            <InputBlock>
              <Field
                name="data.viewCondition"
                render={({ form, field }: FieldProps) => (
                  <React.Fragment>
                    <Input
                      {...field}
                      id={this.viewConditionCheckbox}
                      type="checkbox"
                      label={
                        i18n.t('conditionalView', {
                          ns: 'survey',
                        }) as string
                      }
                      checked={questionData && questionData.viewCondition}
                      onChange={(value) => {
                        form.setFieldValue(
                          'data.viewCondition',
                          value.target.checked
                            ? {
                                refQuestionId: undefined,
                                questionValues: [] as number[],
                              }
                            : undefined
                        );
                      }}
                    />
                  </React.Fragment>
                )}
              />
              {questionData.viewCondition && (
                <div className="row">
                  <div className="col s12 m6">
                    <Field
                      name="data.viewCondition.refQuestionId"
                      type="select"
                      render={({ form }: FieldProps) => (
                        <Select
                          isRequired={true}
                          placeholder={
                            i18n.t('refQuestion', {
                              ns: 'survey',
                            }) as string
                          }
                          label={
                            i18n.t('refQuestion', {
                              ns: 'survey',
                            }) as string
                          }
                          values={
                            questionData.viewCondition.refQuestionId
                              ? [
                                  {
                                    value:
                                      questionData.viewCondition.refQuestionId,
                                    label:
                                      otherQuestions![
                                        questionData.viewCondition.refQuestionId
                                      ]['data'].title,
                                  },
                                ]
                              : ([] as IOption[])
                          }
                          options={Object.values(otherQuestions!)
                            .filter((q) => !q.data.deleted)
                            .reduce((current, question) => {
                              if (
                                question.data.type === 'multipleChoice' &&
                                question.state !== 'creating' &&
                                question.data.order < questionData.order
                              )
                                current.push({
                                  label: question.data.title,
                                  value: question.data.id,
                                });
                              return current;
                            }, [] as IOption[])}
                          onChangeValues={(values) => {
                            const refQuestionId = values[0]
                              ? values[0].value
                              : undefined;
                            form.setFieldValue(
                              'data.viewCondition.refQuestionId',
                              refQuestionId
                            );
                          }}
                        />
                      )}
                    />
                  </div>
                  {questionData.viewCondition.refQuestionId && (
                    <div className="col s12 m6">
                      <Field
                        name="data.viewCondition.questionValues"
                        type="select"
                        render={({ form }: FieldProps) => (
                          <Select
                            isRequired={true}
                            label={
                              i18n.t('questionValues', {
                                ns: 'survey',
                              }) as string
                            }
                            multiChoice={true}
                            values={questionData.viewCondition.questionValues.reduce(
                              (current: IOption[], key: any) => {
                                const foundChoice = conditionChoices.find(
                                  (it) => it.value === key
                                );
                                current.push({
                                  value: key,
                                  label: foundChoice ? foundChoice.text : '',
                                });
                                return current;
                              },
                              []
                            )}
                            options={conditionChoices.reduce(
                              (current: IOption[], choice) => {
                                current.push({
                                  value: choice.value,
                                  label: choice.text,
                                });
                                return current;
                              },
                              []
                            )}
                            onChangeValues={(values) => {
                              const questionValues = values.reduce(
                                (current: string[], value: IOption) => {
                                  current.push(value.value as string);
                                  return current;
                                },
                                []
                              );
                              form.setFieldValue(
                                'data.viewCondition.questionValues',
                                questionValues
                              );
                            }}
                          />
                        )}
                      />
                    </div>
                  )}
                </div>
              )}
            </InputBlock>
          </>
        )}
        {/*<Field
          name="data.viewableWith"
          type="select"
          render={({ form, field }: FieldProps) => (
            <Select
              multiChoice={true}
              options={companyRoleChoices}
              containerClass="col m6 l6 s12"
              values={
                field.value?.map((e: string) => {
                  return companyRoleChoices?.find((it) => it?.value === e);
                }) ?? []
              }
              onChangeValues={(selectedRoles) => {
                form.setFieldValue(
                  'data.viewableWith',
                  selectedRoles.map((r) => r.value)
                );
              }}
              label={messages.viewableWith}
              placeholder={messages.addRole}
            />
          )}
        />*/}
        {children}
        {!isExam && isOwnerOrSupport && (
          <Collapsible
            header={
              i18n.t('advancedOptions', {
                ns: 'survey',
              }) as string
            }
            iconName="settings"
          >
            <div>
              {(!questionData.answerRequired ||
                (questionData.defaultValue &&
                  questionData.defaultValue.length > 0)) && (
                <Field
                  name="data.editableWith"
                  type="select"
                  render={({ form, field }: FieldProps) => (
                    <Select
                      multiChoice={true}
                      options={companyRoleChoices}
                      values={
                        field.value
                          ?.map((e: string) => {
                            return (
                              companyRoleChoices?.find(
                                (it) => it?.value === e
                              ) || { value: e, label: 'Role_' + e }
                            );
                          })
                          .filter((it: IOption) => !!it) ?? []
                      }
                      onChangeValues={(selectedRoles) => {
                        form.setFieldValue(
                          'data.editableWith',
                          selectedRoles.map((r) => r.value)
                        );
                      }}
                      label={
                        i18n.t('editableWith', {
                          ns: 'survey',
                        }) as string
                      }
                      placeholder={
                        i18n.t('addRole', {
                          ns: 'survey',
                        }) as string
                      }
                    />
                  )}
                />
              )}
              {(!questionData.answerRequired ||
                (questionData.defaultValue &&
                  questionData.defaultValue.length > 0)) && (
                <Field
                  name="data.protection"
                  type="select"
                  render={({ form }: FieldProps) => (
                    <Select
                      isRequired={true}
                      placeholder={
                        i18n.t('protection', {
                          ns: 'survey',
                        }) as string
                      }
                      label={
                        i18n.t('protection', {
                          ns: 'survey',
                        }) as string
                      }
                      values={[
                        {
                          value: questionData.protection
                            ? questionData.protection
                            : 'null',
                          label: questionData.protection
                            ? (i18n.t(
                                `${'protection_' + questionData.protection}`,
                                {
                                  ns: 'survey',
                                }
                              ) as string)
                            : (i18n.t('protection_null', {
                                ns: 'survey',
                              }) as string),
                        },
                      ]}
                      options={[
                        {
                          value: 'null',
                          label: i18n.t('protection_null', {
                            ns: 'survey',
                          }) as string,
                        },
                        {
                          value: 'Edit',
                          label: i18n.t('protection_Edit', {
                            ns: 'survey',
                          }) as string,
                        },
                        {
                          value: 'View',
                          label: i18n.t('protection_View', {
                            ns: 'survey',
                          }) as string,
                        },
                      ]}
                      allowRemove={false}
                      onChangeValues={(values) => {
                        const value =
                          values[0].value === 'null' ? null : values[0].value;
                        form.setFieldValue('data.protection', value);
                      }}
                    />
                  )}
                />
              )}
              {(questionData.type === 'multipleChoice' ||
                questionData.type === 'grid') &&
                !questionData.useTags && (
                  <HintBoxWrapper>
                    <Field name="data.unifyValueAndText">
                      {({ field }: FieldProps) => (
                        <Input
                          {...field}
                          id={this.unifyValueAndText}
                          checked={
                            questionData && questionData.unifyValueAndText
                          }
                          type="checkbox"
                          label={
                            i18n.t('unifyValueAndText', {
                              ns: 'survey',
                            }) as string
                          }
                        />
                      )}
                    </Field>
                    <QuestionHintBox>
                      <HintIcon type="info" />
                      <HintText>
                        <span>
                          {
                            i18n.t('unifyValueAndTextHint', {
                              ns: 'survey',
                            }) as string
                          }
                        </span>
                      </HintText>
                    </QuestionHintBox>
                  </HintBoxWrapper>
                )}
              {questionData.type === 'multipleChoice' &&
                questionData.useTags && (
                  <HintBoxWrapper>
                    <Field name="data.readFromItemTag">
                      {({ field }: FieldProps) => (
                        <Input
                          {...field}
                          id={this.readFromItemTag}
                          checked={questionData && questionData.readFromItemTag}
                          type="checkbox"
                          label={
                            i18n.t('readFromItemTag', {
                              ns: 'survey',
                            }) as string
                          }
                        />
                      )}
                    </Field>
                    <QuestionHintBox>
                      <HintIcon type="info" />
                      <HintText>
                        <span>
                          {
                            i18n.t('readFromItemTagHint', {
                              ns: 'survey',
                            }) as string
                          }
                        </span>
                      </HintText>
                    </QuestionHintBox>
                  </HintBoxWrapper>
                )}
            </div>
          </Collapsible>
        )}
      </div>
    );
  }
}

export default connectFormik<IOwnProps, IFormValues>(GeneralQuestionEditor);
